import { useEffect, useRef, useState } from "react";
import { AdminBookingFull } from "@/mocks/admin";

type Status = AdminBookingFull["status"];

type Props = {
  status: Status;
  onChange: (next: Status) => void;
  size?: "sm" | "md";
};

const statusOptions: { id: Status; label: string; icon: string; tint: string }[] = [
  { id: "pending", label: "Needs cleaner", icon: "ri-alert-line", tint: "bg-accent-100 text-accent-800" },
  { id: "scheduled", label: "Scheduled", icon: "ri-calendar-event-line", tint: "bg-secondary-100 text-secondary-800" },
  { id: "in_progress", label: "In progress", icon: "ri-play-circle-line", tint: "bg-primary-100 text-primary-800" },
  { id: "completed", label: "Completed", icon: "ri-checkbox-circle-line", tint: "bg-accent-100 text-accent-800" },
  { id: "cancelled", label: "Cancelled", icon: "ri-close-circle-line", tint: "bg-background-200 text-foreground-700" },
];

export default function StatusMenu({ status, onChange, size = "md" }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = statusOptions.find((s) => s.id === status) || statusOptions[0];

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={ref} className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-full font-medium whitespace-nowrap cursor-pointer hover:opacity-90 transition ${current.tint} ${
          size === "sm" ? "text-[11px] px-2.5 py-1" : "text-xs px-3 py-1.5"
        }`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
        {current.label}
        <i className="ri-arrow-down-s-line text-sm"></i>
      </button>
      {open && (
        <div className="absolute z-50 right-0 mt-1.5 w-52 bg-background-50 border border-background-200 rounded-xl py-1.5 shadow-sm">
          {statusOptions.map((s) => (
            <button
              key={s.id}
              onClick={() => {
                onChange(s.id);
                setOpen(false);
              }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-background-100 cursor-pointer ${
                s.id === status ? "bg-background-100" : ""
              }`}
            >
              <span
                className={`w-7 h-7 flex items-center justify-center rounded-md ${s.tint}`}
              >
                <i className={`${s.icon} text-xs`}></i>
              </span>
              <span className="flex-1 text-left text-foreground-900 font-medium">
                {s.label}
              </span>
              {s.id === status && (
                <i className="ri-check-line text-primary-600"></i>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}